import {
  Banner,
  Button,
  Checkbox,
  CheckboxList,
  DatePicker,
  Form,
  List,
  Modal,
  ModalProps,
  popSuccessToast,
  Radio,
  RadioList,
  TextField
} from '@boltenergy-be/design-system'
import {
  ENERGY_TYPES,
  ERROR_CODES,
  Product,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCT_SWITCH_INSTALMENT_TYPES,
  PRODUCT_TYPES,
  PRODUCTS_CONFIG,
  ServiceContractElectricity,
  ServiceContractGas
} from '@boltenergy-be/shared'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import styles from './ProductSwitchModal.module.scss'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { ProductSwitchInputs } from 'pages/app/tariffs/products/components/product-switch-modal/types.ts'
import dayjs from 'dayjs'
import { DATE_FORMAT, ISO8601_DATE_FORMAT } from 'constants/constants.ts'
import {
  checkHasNightExclusiveMeter,
  checkHasR1R3MeteringRegime,
  checkIsDynamicTariffEligible,
  checkIsMonthlyBillingEligible,
  getPendingMeteringChanges,
  useGetMeterConfigUpdatedEventLogs
} from 'utils/smartBillingOptions.ts'
import { selectCurrentAccount, selectCurrentBillingContract, updateSelectedServiceContracts } from 'store/contact/slice.ts'
import { useMemo, useState } from 'react'
import backendLib from 'lib/bolt-backend'
import parse from 'html-react-parser'
import { isActiveContract } from 'utils/contracts.ts'
import { roundToTwoDecimals } from 'utils/number.ts'
import { formatCurrency } from 'utils/format.ts'
import { determineCurrentSmartMeterOptions } from 'pages/app/tariffs/products/utils.ts'
import { selectPriceLists } from 'store/contact/selectors.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { PendingMeteringChange } from 'pages/app/addresses/offtake-address/types'
import { boltApi } from 'store/queries/bolt-api'
import { TagTypes } from 'store/queries/bolt-api/types'

const ProductSwitchModal = ({
  isOpen,
  onRequestClose,
  newProduct,
  isRenew
}: Pick<ModalProps, 'isOpen' | 'onRequestClose'> & { newProduct: Product | null; isRenew: boolean }) => {
  // Redux
  const { userType } = useStoreSelector((state) => state.auth)
  const { contact } = useStoreSelector((store) => store.contact)
  const account = useStoreSelector(selectCurrentAccount)
  const billingContract = useStoreSelector(selectCurrentBillingContract)
  const serviceContracts = billingContract.serviceContracts
  const priceLists = useStoreSelector(selectPriceLists)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation('tariffs', { keyPrefix: 'switchModal' })
  const { t: tValidation } = useTranslation('validation')
  const language = useGetLanguage()

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [referenceDatePickerOpen, setReferenceDatePickerOpen] = useState<boolean>(false)
  const [renewConfirmation, setRenewConfirmation] = useState<boolean>(false)

  // Constants
  const monthlyBillingEligible = checkIsMonthlyBillingEligible({ serviceContracts, product: newProduct || undefined })
  const dynamicTariffEligible = checkIsDynamicTariffEligible({ serviceContracts, product: newProduct || undefined })
  const currentElectricityInstalment = isActiveContract(serviceContracts.electricity)
    ? Number(serviceContracts.electricity.detail.instalment || 0)
    : 0
  const currentGasInstalment =
    serviceContracts.gas && isActiveContract(serviceContracts.gas) ? Number(serviceContracts.gas?.detail.instalment || 0) : 0
  const currentInstalment = roundToTwoDecimals(currentElectricityInstalment + currentGasInstalment)
  const activeAttestation = account?.attestations?.length
    ? account.attestations.find(
        ({ ean, fromDate, toDate }) =>
          ean === serviceContracts.electricity.deliveryPoint.ean && dayjs().isBetween(dayjs(fromDate), dayjs(toDate))
      )
    : undefined
  const latestFuturePriceList = priceLists?.electricity?.future?.find((list) => !list.toDate)
  const allowedStartDate = latestFuturePriceList?.fromDate
    ? dayjs(latestFuturePriceList?.fromDate).add(1, 'day').format(ISO8601_DATE_FORMAT)
    : priceLists?.electricity?.current?.toDate || dayjs().add(1, 'day').format(ISO8601_DATE_FORMAT)
  const firstOfNextMonth = dayjs().add(1, 'month').startOf('month')
  const firstOfNextMonthOfStartDate =
    !latestFuturePriceList && dayjs(allowedStartDate).isSame(firstOfNextMonth, 'day')
      ? firstOfNextMonth.format(ISO8601_DATE_FORMAT)
      : dayjs(allowedStartDate).add(1, 'month').startOf('month').format(ISO8601_DATE_FORMAT)
  const maximumAllowedDate = dayjs(allowedStartDate).add(4, 'months').startOf('month')

  // React hook form (After constants to use in default values)
  const { register, handleSubmit, watch, reset, setValue, formState, clearErrors, resetField, getValues } = useForm<ProductSwitchInputs>({
    mode: 'onSubmit',
    defaultValues: {
      startDate: firstOfNextMonthOfStartDate,
      referenceDate: undefined,
      instalmentType: PRODUCT_SWITCH_INSTALMENT_TYPES.unchanged,
      smartMeterOptions: determineCurrentSmartMeterOptions(serviceContracts, monthlyBillingEligible)
    }
  })
  const watchInstalment = watch('instalmentType')
  const watchStartDate = watch('startDate')
  const watchReferenceDate = watch('referenceDate')
  const startDateAfterContractualEndDate = serviceContracts.electricity.detail.contractualEndDate
    ? dayjs(watchStartDate).isAfter(serviceContracts.electricity.detail.contractualEndDate)
    : false
  const watchSmartMeterOptions = watch('smartMeterOptions')

  const userEventLogsData = useGetMeterConfigUpdatedEventLogs(contact?.email, userType)

  const pendingMeteringChange = useMemo<PendingMeteringChange | undefined>(() => {
    return getPendingMeteringChanges(userEventLogsData, serviceContracts.electricity.contractNumber)
  }, [userEventLogsData, serviceContracts.electricity.contractNumber])

  /**
   * Closes the modal and resets the form state
   */
  const closeModal = () => {
    reset()
    setError('')
    setLoading(false)
    clearErrors()
    onRequestClose()
  }

  /**
   * Checks if the product switch requires confirmation before submission
   * @param {ProductSwitchInputs} data
   */
  const checkNeedsConfirmation = (data: ProductSwitchInputs) => {
    if (isRenew) {
      setRenewConfirmation(true)
    } else {
      void submit(data)
    }
  }

  /**
   * Handles the submission of the product switch form
   * @param {ProductSwitchInputs} data
   */
  const submit = async (data: ProductSwitchInputs) => {
    setLoading(true)
    setError('')

    const eligibility = {
      [ENERGY_TYPES.electricity]: {
        monthlyBilling: monthlyBillingEligible.electricity,
        dynamicTariff: dynamicTariffEligible
      },
      [ENERGY_TYPES.gas]: serviceContracts?.gas
        ? {
            monthlyBilling: monthlyBillingEligible.gas || false,
            dynamicTariff: false // Gas contracts can not have dynamic tariff
          }
        : undefined
    }

    if (!newProduct) {
      throw new Error('Product is required for switch product')
    }

    try {
      if (data.instalmentType !== PRODUCT_SWITCH_INSTALMENT_TYPES.fixedAmount && data.newTotalInstalmentAmount) {
        delete data.newTotalInstalmentAmount
      }

      const res = await backendLib.switchProduct({
        billingContract,
        eligibility,
        inputs: data,
        product: newProduct,
        userType
      })

      if ('success' in res && !res.success) {
        setError(res.errorCode || 'Product switch failed')
        return
      }

      if (ENERGY_TYPES.electricity in res) {
        dispatch(
          updateSelectedServiceContracts({
            electricity: res[ENERGY_TYPES.electricity] as ServiceContractElectricity,
            gas: res[ENERGY_TYPES.gas] as ServiceContractGas | undefined
          })
        )

        closeModal()
        setValue('startDate', dayjs(data.startDate).add(1, 'day').format(ISO8601_DATE_FORMAT))
        popSuccessToast(t(($) => $.successMessage))
      }
      dispatch(boltApi.util.invalidateTags([TagTypes.EVENT_LOGS]))
    } catch (err) {
      setError(err instanceof Error ? err?.message : 'Unknown error in switchProduct')
      log({
        error: err instanceof Error ? err : 'Unknown error in switchProduct',
        identifier: `[${Flow.TARIFFS}:ProductSwitchModal:submit]`,
        metaData: {
          errorPayload: {
            err,
            billingContractId: billingContract.contractNumber,
            inputs: data,
            eligibility
          }
        }
      })
    } finally {
      setLoading(false)
    }
  }

  /**
   * Returns the error text based on the error code
   *
   * @param {string} err
   * @returns {string}
   */
  const getErrorText = (err: string): string => {
    switch (err) {
      case ERROR_CODES.invalidContractStatusForOperation:
        return t(($) => $.errors.invalidStatus)

      default:
        return t(($) => $.errors.common)
    }
  }

  /**
   * Format the smart meter options warning message
   * @returns {string}
   */
  const getSmartMeterOptionsWarnings = (): string[] => {
    const reasons = []

    if (newProduct && PRODUCTS_CONFIG[newProduct][PRODUCT_CONFIG_OPTIONS.type] === PRODUCT_TYPES.fix) {
      reasons.push(t(($) => $.smartMeterOptions.unavailableReasons.fixedTariff))
    }

    if (checkHasNightExclusiveMeter(serviceContracts.electricity)) {
      reasons.push(t(($) => $.smartMeterOptions.unavailableReasons.exclusiveNight))
    }

    if (!checkHasR1R3MeteringRegime(serviceContracts.electricity)) {
      reasons.push(t(($) => $.smartMeterOptions.unavailableReasons.meteringRegime))
    }

    if (pendingMeteringChange?.monthlyBilling?.active) {
      reasons.push(t(($) => $.smartMeterOptions.unavailableReasons.monthlyPending))
    }

    if (pendingMeteringChange?.dynamicTariff?.active) {
      reasons.push(t(($) => $.smartMeterOptions.unavailableReasons.dynamicPending))
    }

    return reasons
  }

  return (
    <>
      <Modal
        isOpen={isOpen && !renewConfirmation}
        onRequestClose={closeModal}
        title={newProduct ? PRODUCTS_CONFIG[newProduct][PRODUCT_CONFIG_OPTIONS.name][language] : ''}
      >
        {!!activeAttestation && (
          <Banner type="informative" title={t(($) => $.socialTariff.title)}>
            {t(($) => $.socialTariff.description, {
              date: dayjs(activeAttestation.toDate).format(DATE_FORMAT),
              interpolation: { escapeValue: false }
            })}
          </Banner>
        )}

        <Form className={styles.form} onSubmit={handleSubmit(checkNeedsConfirmation)}>
          <div id="cal-root" className={styles['calendar-portal']} />
          <DatePicker
            {...{ language }}
            label={t(($) => $.date.label)}
            description={t(($) => $.date.description)}
            dateFormat="dd/MM/yyyy"
            portalId="cal-root"
            name="startDate"
            minDate={dayjs(allowedStartDate).toDate()}
            maxDate={maximumAllowedDate.toDate()}
            error={formState.errors?.startDate?.message}
            selected={dayjs(watchStartDate).toDate()}
            onChange={(date) => {
              if (typeof date === 'string' || typeof date === 'object') {
                const isValidDate = dayjs(date as string).isValid()

                if (isValidDate) {
                  setValue('startDate', dayjs(date as string).format(ISO8601_DATE_FORMAT), { shouldValidate: true })
                }
              }
            }}
            prefix={{ as: 'calendar' }}
          />

          {!isRenew && (
            <div className={styles['reference-date-wrapper']}>
              <Button
                representation="link"
                size="small"
                type="button"
                variant="tertiary"
                onClick={() => setReferenceDatePickerOpen(!referenceDatePickerOpen)}
              >
                {t(($) => $.referenceDate.button)}
              </Button>
              {referenceDatePickerOpen && (
                <DatePicker
                  {...{ language }}
                  label={t(($) => $.referenceDate.label)}
                  description={t(($) => $.referenceDate.description)}
                  dateFormat="dd/MM/yyyy"
                  portalId="cal-root"
                  name="referenceDate"
                  minDate={dayjs().subtract(1, 'month').toDate()}
                  maxDate={dayjs().toDate()}
                  error={formState.errors?.referenceDate?.message}
                  selected={dayjs(watchReferenceDate).toDate()}
                  onChange={(date) => {
                    if (typeof date === 'string' || typeof date === 'object') {
                      const isValidDate = dayjs(date as string).isValid()

                      if (isValidDate) {
                        setValue('referenceDate', dayjs(date as string).format(ISO8601_DATE_FORMAT), { shouldValidate: true })
                      }
                    }
                  }}
                  prefix={{ as: 'calendar' }}
                />
              )}
            </div>
          )}

          <RadioList label={t(($) => $.instalment.label)} description={t(($) => $.instalment.description)}>
            <Radio
              {...register('instalmentType', {
                onChange: () => {
                  resetField('newTotalInstalmentAmount')
                }
              })}
              id={PRODUCT_SWITCH_INSTALMENT_TYPES.unchanged}
              value={PRODUCT_SWITCH_INSTALMENT_TYPES.unchanged}
              label={parse(
                t(($) => $.instalment.options.maintain, {
                  context: currentInstalment ? 'with' : 'without',
                  amount: currentInstalment
                })
              )}
            />
            <Radio
              {...register('instalmentType', {
                onChange: () => {
                  resetField('newTotalInstalmentAmount')
                }
              })}
              id={PRODUCT_SWITCH_INSTALMENT_TYPES.eav}
              value={PRODUCT_SWITCH_INSTALMENT_TYPES.eav}
              label={t(($) => $.instalment.options.eav)}
            />
            <Radio
              {...register('instalmentType')}
              wrapperClassName={styles['fixed-amount-radio']}
              id={PRODUCT_SWITCH_INSTALMENT_TYPES.fixedAmount}
              value={PRODUCT_SWITCH_INSTALMENT_TYPES.fixedAmount}
              label={
                <>
                  <span className={styles.label}>{t(($) => $.instalment.options.fixedAmount)}</span>
                  <TextField
                    {...register('newTotalInstalmentAmount', {
                      onChange: (e) =>
                        setValue('newTotalInstalmentAmount', roundToTwoDecimals(Number(e.target.value)), { shouldValidate: true }),
                      required: watchInstalment === PRODUCT_SWITCH_INSTALMENT_TYPES.fixedAmount ? tValidation(($) => $.required) : false,
                      min: {
                        value: 20,
                        message: tValidation(($) => $.instalment.min, {
                          amount: formatCurrency(20, { language })
                        })
                      }
                    })}
                    prefix={{ as: '€' }}
                    label={t(($) => $.instalment.fixedInstalmentPlaceholder)}
                    placeholder={t(($) => $.instalment.fixedInstalmentPlaceholder)}
                    hiddenLabel
                    error={formState.errors.newTotalInstalmentAmount?.message}
                    type="number"
                    step="0.01"
                    disabled={watchInstalment !== PRODUCT_SWITCH_INSTALMENT_TYPES.fixedAmount}
                    layoutClassName={styles['new-instalment-field']}
                  />
                </>
              }
            />
          </RadioList>

          <CheckboxList label={t(($) => $.smartMeterOptions.label)} description={t(($) => $.smartMeterOptions.options.description)}>
            <div className={styles['smart-meter-options']}>
              <Checkbox
                {...register('smartMeterOptions')}
                disabled={!monthlyBillingEligible.electricity || !!pendingMeteringChange?.monthlyBilling?.active}
                id="monthlyBilling"
                value="monthlyBilling"
                label={t(($) => $.smartMeterOptions.options.monthlyBilling)}
              />
              {watchSmartMeterOptions.includes('monthlyBilling') && (
                <span>
                  {t(($) => $.smartMeterOptions.options.dateNotification, {
                    date: dayjs(allowedStartDate).add(1, 'month').startOf('month').format(DATE_FORMAT),
                    interpolation: { escapeValue: false }
                  })}
                </span>
              )}
            </div>
            <div className={styles['smart-meter-options']}>
              <Checkbox
                {...register('smartMeterOptions')}
                disabled={!dynamicTariffEligible || !!pendingMeteringChange?.dynamicTariff?.active}
                id="dynamicTariff"
                value="dynamicTariff"
                label={t(($) => $.smartMeterOptions.options.dynamicTariff)}
              />
              {watchSmartMeterOptions.includes('dynamicTariff') && (
                <span>
                  {t(($) => $.smartMeterOptions.options.dateNotification, {
                    date: dayjs(allowedStartDate).add(1, 'month').startOf('month').format(DATE_FORMAT),
                    interpolation: { escapeValue: false }
                  })}
                </span>
              )}
            </div>
          </CheckboxList>

          {(!monthlyBillingEligible ||
            !dynamicTariffEligible ||
            pendingMeteringChange?.dynamicTariff?.active ||
            pendingMeteringChange?.monthlyBilling?.active) && (
            <Banner type="warning" title={t(($) => $.smartMeterOptions.warning)}>
              <List as="ul" className={styles.list}>
                {getSmartMeterOptionsWarnings().map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </List>
            </Banner>
          )}

          <Form.Footer>
            <Button disabled={!!error || startDateAfterContractualEndDate} loading={loading}>
              {t(($) => $.action)}
            </Button>
          </Form.Footer>
        </Form>

        {startDateAfterContractualEndDate && (
          <Banner
            type="blocking"
            title={t(($) => $.errors.afterContractualEndDate, {
              date: dayjs(serviceContracts.electricity.detail.contractualEndDate).format(DATE_FORMAT),
              interpolation: { escapeValue: false }
            })}
          />
        )}

        {error && <Banner type="blocking" title={getErrorText(error)} />}
      </Modal>

      <Modal isOpen={renewConfirmation} onRequestClose={() => setRenewConfirmation(false)} title={t(($) => $.renewConfirmation.title)}>
        <p>{t(($) => $.renewConfirmation.description)}</p>

        <Button
          type="button"
          onClick={() => {
            const data = getValues()
            void submit(data)
          }}
          className="ml-auto"
        >
          {t(($) => $.renewConfirmation.action)}
        </Button>
      </Modal>
    </>
  )
}

export default ProductSwitchModal
